import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

const DESKTOP = {
  padding: 24,
  titleHeight: 76,
  breadcrumbsHeight: 24,
  footerHeight: 0,
  tableFooterHeight: 72,
  tableRowHeight: 72,
  tableBulkActionsHeight: 75,
  tableBulkActionsTop: 94,
};

const TABLET = {
  padding: 12,
  titleHeight: 48,
  breadcrumbsHeight: 20,
  footerHeight: 0,
  tableFooterHeight: 48,
  tableRowHeight: 52,
  tableBulkActionsHeight: 55,
  tableBulkActionsTop: 98,
};

const MOBILE = {
  padding: 8,
  titleHeight: 40,
  breadcrumbsHeight: 40,
  footerHeight: 72,
  tableFooterHeight: 56,
  tableRowHeight: 44,
  tableBulkActionsHeight: 52,
  tableBulkActionsTop: 82,
};

interface DashboardLayoutContextValue {
  padding: number;
  titleHeight: number;
  breadcrumbsHeight: number;
  usedHeight: number;
  footerHeight: number;
  tableFooterHeight: number;
  tableRowHeight: number;
  tableBulkActionsHeight: number;
  tableBulkActionsTop: number;
  mobileHeaderActionsTarget: HTMLDivElement | null;
  setMobileHeaderActionsTarget: (element: HTMLDivElement | null) => void;
}

const DashboardLayoutContext = createContext<DashboardLayoutContextValue>(undefined!);

/**
 * Provides DashboardLayout dimension constants to the component tree.
 * Values adapt automatically between desktop and tablet breakpoints.
 */
export function DashboardLayoutProvider({ children }: { children: ReactNode }) {
  const { isMobile, isTablet } = useResponsiveDesign();
  const [mobileHeaderActionsTarget, setMobileHeaderActionsTargetState] = useState<HTMLDivElement | null>(null);

  const setMobileHeaderActionsTarget = useCallback((element: HTMLDivElement | null) => {
    setMobileHeaderActionsTargetState(element);
  }, []);

  const value = useMemo(() => {
    const tokens = isMobile ? MOBILE : isTablet ? TABLET : DESKTOP;
    const { padding, titleHeight, breadcrumbsHeight, footerHeight } = tokens;

    return {
      ...tokens,
      usedHeight: padding * 2 + titleHeight + breadcrumbsHeight + footerHeight,
      mobileHeaderActionsTarget,
      setMobileHeaderActionsTarget,
    };
  }, [isMobile, isTablet, mobileHeaderActionsTarget, setMobileHeaderActionsTarget]);

  return <DashboardLayoutContext.Provider value={value}>{children}</DashboardLayoutContext.Provider>;
}

/**
 * Returns DashboardLayout dimension constants (padding, heights).
 */
export function useDashboardLayout() {
  return useContext(DashboardLayoutContext);
}
